import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize the API with your key
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GOOGLE_API_KEY);

// Function to generate study notes
export async function generateNotes(topic) {
  try {
    const model = genAI.getGenerativeModel({ 
      model: "gemini-1.5-pro",
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048,
      }
    });
    
    const prompt = `Create detailed study notes for the topic: ${topic}. 
    Include key concepts, definitions, and examples.
    Format the response in markdown with proper headings, lists, and emphasis.
    
    Example format:
    # Topic Name
    ## Key Concepts
    - Point 1
    - Point 2
    
    ## Definitions
    - Term 1: Definition
    - Term 2: Definition
    
    ## Examples
    1. First example
    2. Second example`;
    
    const result = await model.generateContent(prompt);
    const text = result.response.text();
    
    // Ensure the response is valid markdown
    if (!text || text.trim() === '') {
      return '# Error\n\nFailed to generate study notes. Please try again.';
    }
    
    return text;
  } catch (error) {
    console.error('Failed to generate notes:', error);
    return '# Error\n\nFailed to generate study notes. Please try again.';
  }
}

// Function to generate quiz cards
export async function generateQuizCards(topic) {
  try {
    const model = genAI.getGenerativeModel({ 
      model: "gemini-1.5-pro",
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048,
      }
    });
    
    const prompt = `Create 10 true/false questions about ${topic}.
    Return only a JSON array where each object has exactly two properties:
    - "question": a string containing the true/false statement
    - "correct": a boolean (true/false) indicating if the statement is true
    
    Format example:
    [
      {
        "question": "The sky appears blue due to Rayleigh scattering",
        "correct": true
      }
    ]
    
    Important: Return only the JSON array, no other text or formatting.`;
    
    const result = await model.generateContent(prompt);
    const text = result.response.text();
    
    try {
      // Clean the text to ensure it's valid JSON
      const cleanText = text.trim().replace(/^```json\s*|\s*```$/g, '');
      const parsed = JSON.parse(cleanText);
      
      if (!Array.isArray(parsed)) {
        throw new Error('Response is not an array');
      }
      
      // Validate each question object
      const validQuestions = parsed.filter(q => 
        typeof q.question === 'string' && 
        typeof q.correct === 'boolean'
      );
      
      if (validQuestions.length === 0) {
        return [{
          question: "There was an error generating questions. Would you like to try again?",
          correct: true
        }];
      }
      
      return validQuestions;
    } catch (parseError) {
      console.error('Failed to parse quiz data:', parseError, 'Raw text:', text);
      // Return a default question if parsing fails
      return [{
        question: "There was an error generating questions. Would you like to try again?",
        correct: true
      }];
    }
  } catch (error) {
    console.error('Failed to generate quiz:', error);
    return [{
      question: "There was an error connecting to the service. Please check your API key and try again.",
      correct: true
    }];
  }
}

// Function to generate learning roadmap
export async function generateRoadmap(topic) {
  try {
    const model = genAI.getGenerativeModel({ 
      model: "gemini-1.5-pro",
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048,
      }
    });
    
    const prompt = `Create a detailed learning roadmap for ${topic}.
    Return a JSON array where each object represents a topic or subtopic with the following structure:
    {
      "id": "unique-id",
      "title": "Topic title",
      "description": "Brief description of what will be covered",
      "subtopics": [] // Array of similar objects for subtopics
    }
    
    The roadmap should be structured in a logical learning sequence, from basics to advanced concepts.
    Include 3-5 main topics, each with 2-3 subtopics.
    
    Important: Return only the JSON array, no other text or formatting.`;
    
    const result = await model.generateContent(prompt);
    const text = result.response.text();
    
    try {
      // Clean the text to ensure it's valid JSON
      const cleanText = text.trim().replace(/^```json\s*|\s*```$/g, '');
      const parsed = JSON.parse(cleanText);
      
      if (!Array.isArray(parsed)) {
        throw new Error('Response is not an array');
      }
      
      // Add IDs if they're missing
      const addIds = (items, parentId = '') => {
        return items.map((item, index) => {
          const id = item.id || `${parentId}${index + 1}`;
          return {
            ...item,
            id,
            subtopics: item.subtopics ? addIds(item.subtopics, `${id}-`) : []
          };
        });
      };
      
      return addIds(parsed);
    } catch (parseError) {
      console.error('Failed to parse roadmap data:', parseError, 'Raw text:', text);
      return [{
        id: 'error',
        title: 'Error generating roadmap',
        description: 'There was an error generating the roadmap. Please try again.',
        subtopics: []
      }];
    }
  } catch (error) {
    console.error('Failed to generate roadmap:', error);
    return [{
      id: 'error',
      title: 'Connection Error',
      description: 'Failed to connect to the service. Please check your API key and try again.',
      subtopics: []
    }];
  }
}